import PublicLayout from "@/components/PublicLayout";
import ContactForm from "@/components/ContactForm";
import AppointmentForm from "@/components/AppointmentForm";
import { CONTACT } from "@/lib/config";
import { Mail, Phone, MapPin, Clock, MessageCircle } from "lucide-react";
import { useState } from "react";

export default function Contact() {
  const [tab, setTab] = useState("contact");

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-6">
        <div className="text-xs uppercase tracking-widest text-blue-700">Contact</div>
        <h1 className="h-display mt-3 text-5xl md:text-6xl text-slate-900 max-w-4xl">Let's talk about your books, taxes and next steps.</h1>
        <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">
          Send us a message, book an appointment, or chat with us on WhatsApp. Senior advisors reply within one business day.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center"><MapPin size={18} /></div>
              <div className="flex-1">
                <div className="text-sm text-slate-500 uppercase tracking-widest">Office</div>
                <div className="mt-1 text-slate-900">{CONTACT.address}</div>
                <a href={CONTACT.mapLink} target="_blank" rel="noreferrer" data-testid="contact-directions" className="mt-3 inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900">Get directions →</a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-info-phone" className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-400 transition-colors">
              <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-3"><Phone size={18} /></div>
              <div className="text-sm text-slate-500 uppercase tracking-widest">Phone</div>
              <div className="mt-1 text-slate-900">{CONTACT.phone}</div>
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-info-email" className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-400 transition-colors">
              <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-3"><Mail size={18} /></div>
              <div className="text-sm text-slate-500 uppercase tracking-widest">Email</div>
              <div className="mt-1 text-slate-900">{CONTACT.email}</div>
            </a>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-3"><Clock size={18} /></div>
              <div className="text-sm text-slate-500 uppercase tracking-widest">Hours</div>
              <div className="mt-1 text-slate-900">{CONTACT.hours}</div>
            </div>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 hover:border-emerald-400 transition-colors">
              <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center mb-3"><MessageCircle size={18} /></div>
              <div className="text-sm text-emerald-700 uppercase tracking-widest">WhatsApp</div>
              <div className="mt-1 text-emerald-900">Chat with us</div>
            </a>
          </div>

          <div className="rounded-2xl overflow-hidden border border-slate-200">
            <iframe
              title="Rajkot map"
              data-testid="contact-map"
              src={CONTACT.mapEmbed}
              className="w-full h-72 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setTab("contact")}
                data-testid="tab-contact"
                className={`px-5 py-2 rounded-full text-sm border transition-colors ${
                  tab === "contact" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
                }`}
              >Send Enquiry</button>
              <button
                onClick={() => setTab("appointment")}
                data-testid="tab-appointment"
                className={`px-5 py-2 rounded-full text-sm border transition-colors ${
                  tab === "appointment" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-blue-400"
                }`}
              >Book Appointment</button>
            </div>
            {tab === "contact" ? <ContactForm /> : <AppointmentForm />}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
